import {
  LIGHT_CHAIN,
  HEAVY_FINISHER,
  HEAVY_STANDALONE,
  HEAVY_CHARGED,
  type MoveDef,
} from '@/data/moves';

export type AttackButton = 'L' | 'H';

/**
 * ComboTracker — pure combo state machine.
 *
 * Feed it button presses with the current frame number; it returns the MoveDef
 * to execute. A press "links" to the current string only if it lands within the
 * cancel window. Light presses escalate L1→L2→L3 (then wrap to L1); a Heavy
 * mid-string is the launcher finisher, a cold Heavy is standalone (or charged
 * if the caller signals the hold threshold was met).
 *
 * Frame-driven + framework-free → the whole timing matrix is unit-testable.
 */
export class ComboTracker {
  private chain = 0;

  private lastFrame = Number.NEGATIVE_INFINITY;

  constructor(private readonly windowFrames = 24) {}

  /** Is the next press still within the cancel window at `frame`? */
  inWindowAt(frame: number): boolean {
    return frame - this.lastFrame <= this.windowFrames;
  }

  /** Current light-chain position (0 = no active string). */
  get comboCount(): number {
    return this.chain;
  }

  press(button: AttackButton, frame: number, charged = false): MoveDef {
    const linked = this.chain > 0 && frame - this.lastFrame <= this.windowFrames;
    this.lastFrame = frame;

    if (button === 'L') {
      this.chain = linked && this.chain < LIGHT_CHAIN.length ? this.chain + 1 : 1;
      return LIGHT_CHAIN[this.chain - 1];
    }

    // Heavy.
    if (linked) {
      this.chain = 0;
      return HEAVY_FINISHER;
    }
    this.chain = 0;
    return charged ? HEAVY_CHARGED : HEAVY_STANDALONE;
  }

  reset(): void {
    this.chain = 0;
    this.lastFrame = Number.NEGATIVE_INFINITY;
  }
}
